import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { apiGet, apiPost, apiPostStream, apiPut } from '../lib/api-client';
import { EmailTemplateSelect } from '../components/documents/TemplateSelects';
import { apiError } from '../lib/utils';
import { EmailFields } from '../components/documents/EmailFields';
import { EmailPreview } from '../components/documents/EmailPreview';
import { InvoiceFormat2 } from '../components/documents/InvoiceFormat2';
import { InvoicePreview } from '../components/documents/InvoicePreview';
import { Alert } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { useActivity } from '../components/ui/Activity';
import { useNotice } from '../components/ui/Notice';
import { PageHead, SectionCard } from '../components/ui/chrome';
import { defaultSender, type CompanyMailbox } from '../lib/company-mail';
import { supplierOrderVatPence } from '../lib/money';

interface PoRow {
  id: string;
  poNumber?: string;
  status?: string;
  direction?: string;
  vendorName?: string;
  customerName?: string;
  customerId?: string;
  vendorId?: string;
  layout?: string;
  currency?: string;
  issuedOn?: string;
  billToName?: string;
  billToAddress?: string;
  billToEmail?: string;
  contactName?: string;
  contactRole?: string;
  contactEmail?: string;
  contactPhone?: string;
  supplierVat?: 'none' | 'standard';
  paymentTerms?: string;
  delivery?: string;
  notice?: string;
  acceptance?: string;
  emailSubject?: string;
  emailBody?: string;
  lines?: { reference: string; description: string; qty?: number; unitPence?: number; amountPence: number }[];
}

export function PurchaseOrderReviewPage() {
  const notice = useNotice();
  const activity = useActivity();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const supplier = location.pathname.startsWith('/supplier-purchase-orders');
  const back = `/supplier-purchase-orders/${id}`;
  const list = '/supplier-purchase-orders';
  const [row, setRow] = useState<PoRow | null>(null);
  const [from, setFrom] = useState<CompanyMailbox>(defaultSender('supplier-order'));
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!supplier) {
      if (id) navigate(`/purchase-orders/${id}`, { replace: true });
      return;
    }
    if (!id) return;
    const url = '/api/purchase-orders?direction=supplier';
    void Promise.all([
      apiGet<PoRow[]>(url),
      apiGet<{ id: string; emails?: string[]; billingEmails?: string[] }[]>(supplier ? '/api/vendors' : '/api/customers'),
    ]).then(([orders, parties]) => {
      const found = orders.find((item) => item.id === id);
      if (!found) { setError('Purchase order not found'); return; }
      setRow(found);
      const party = parties.find((item) => item.id === (supplier ? found.vendorId : found.customerId));
      const emails = supplier ? party?.emails || [] : party?.billingEmails || [];
      setTo(emails);
      setSubject(found.emailSubject || `Purchase order ${found.poNumber || ''} from Maxiron Ltd`);
      setBody(found.emailBody || '');
    }).catch((err) => setError(apiError(err, 'Could not open review')));
  }, [id, supplier, navigate]);

  async function issue(send: boolean) {
    if (!row) return;
    await apiPut(`/api/purchase-orders/${row.id}`, { emailSubject: subject, emailBody: body });
    const payload = {
      send,
      to,
      cc,
      emailSubject: subject,
      emailBody: body,
      contactName: row.contactName,
      contactRole: row.contactRole,
      contactEmail: row.contactEmail,
      contactPhone: row.contactPhone,
      from,
    };
    if (!send) {
      const sent = await notice.run('Issuing the purchase order', () => apiPost(`/api/purchase-orders/${row.id}/issue`, payload), 'Marked issued. No email was sent.');
      if (sent !== undefined) setRow({ ...row, status: 'issued' });
      return;
    }
    const number = row.poNumber || 'Purchase order';
    const issued = await activity.track({
      title: 'Sending your purchase order',
      subtitle: subject || number,
      dockTitle: `Sending purchase order ${number}`,
      to: to.join(', '),
      attachments: 'Purchase order',
      kindLabel: 'Purchase order',
      steps: [
        { id: 'documents', label: 'Documents prepared', detail: number },
        { id: 'issued', label: 'Purchase order issued' },
        { id: 'email', label: 'Sending email', detail: 'Waiting for the mail service to confirm.' },
      ],
    }, (apply, signal) => apiPostStream(`/api/purchase-orders/${row.id}/issue`, { ...payload, progress: true }, apply, {}, signal));
    if (issued) setRow({ ...row, status: 'issued' });
  }

  if (!supplier) return null;

  const issued = row?.status === 'issued';
  const party = row?.vendorName || '';
  const lines = (row?.lines || []).map((line) => {
    const qty = line.qty && line.qty > 0 ? line.qty : 1;
    const reference = (line.reference || '').trim();
    const description = (line.description || '').trim();
    const unit = typeof line.unitPence === 'number' ? line.unitPence : Math.round(line.amountPence / qty);
    return { qty, description: reference && description ? `${reference} - ${description}` : reference || description, unitPence: unit, amountPence: line.amountPence };
  });
  const emails = to;
  const previewVat = supplierOrderVatPence(lines.reduce((sum, line) => sum + line.amountPence, 0), row?.supplierVat);
  const preview = row?.layout === '1'
    ? <InvoicePreview documentTitle="PURCHASE ORDER" numberLabel="PO NO." number={row?.poNumber} customerName={party} billingEmails={emails} billToName={row?.billToName || ''} billToAddress={row?.billToAddress || ''} billToEmail={row?.billToEmail || ''} issuedOn={row?.issuedOn || ''} dueOn="" currency={row?.currency || 'GBP'} poNumber={row?.poNumber || ''} contactName={row?.contactName || ''} contactRole={row?.contactRole || ''} contactEmail={row?.contactEmail || ''} contactPhone={row?.contactPhone || ''} paymentTerms={row?.paymentTerms || ''} delivery={row?.delivery || ''} notice={row?.notice || ''} acceptance={row?.acceptance || ''} lines={lines} vatPence={previewVat} />
    : <InvoiceFormat2 documentTitle="PURCHASE ORDER" number={row?.poNumber} customerName={party} billingEmails={emails} billToName={row?.billToName || ''} billToAddress={row?.billToAddress || ''} billToEmail={row?.billToEmail || ''} issuedOn={row?.issuedOn || ''} dueOn="" currency={row?.currency || 'GBP'} poNumber="" contactName={row?.contactName || ''} contactRole={row?.contactRole || ''} contactEmail={row?.contactEmail || ''} contactPhone={row?.contactPhone || ''} paymentTerms={row?.paymentTerms || ''} delivery={row?.delivery || ''} notice={row?.notice || ''} acceptance={row?.acceptance || ''} lines={lines} vatPence={previewVat} />;

  return (
    <div>
      <PageHead
        crumbs={[{ label: 'Purchase orders', to: list }, { label: row?.poNumber || 'Order', to: back }, { label: 'Review & issue' }]}
        back={{ label: 'Back to order', to: back }}
        title="Review & issue"
        subtitle="Check the purchase order and the message before sending."
        actions={
          <>
            <Button variant="outline" disabled={!row || issued} onClick={() => void issue(false)}>Issue without email</Button>
            <Button disabled={!row || issued} onClick={() => void issue(true)}>Issue & send</Button>
          </>
        }
      />
      {error ? <Alert error>{error}</Alert> : null}
      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <SectionCard title="Email" hint="The message sent with the purchase order.">
            <EmailTemplateSelect kind="purchase_order" values={{ number: row?.poNumber || '', customer: row?.vendorName || '', title: row?.lines?.[0]?.description || '' }} onApply={(nextSubject, nextBody) => { setSubject(nextSubject); setBody(nextBody); }} />
            <EmailFields from={from} to={to} cc={cc} subject={subject} body={body} onFrom={setFrom} onTo={setTo} onCc={setCc} onSubject={setSubject} onBody={setBody} />
          </SectionCard>
          <EmailPreview from={from} recipients={to.join(', ')} cc={cc.join(', ')} subject={subject} body={body} senderName={row?.contactName} senderTitle={row?.contactRole} senderEmail={row?.contactEmail} senderPhone={row?.contactPhone} />
        </div>
        <div className="min-h-0 overflow-y-auto rounded-lg bg-[var(--bo-preview)] p-3">
          <div className="mb-3"><Button variant="outline" onClick={() => row && window.open(`/api/purchase-orders/${row.id}/pdf`, '_blank')}>Download</Button></div>
          {row ? preview : null}
        </div>
      </div>
      <p className="mt-4 text-sm text-[var(--bo-muted)]">Issuing records the recipient and time. An issued order cannot be edited.</p>
    </div>
  );
}
